// Добавление документа. Панель: документ можно завести заново, необратимого тут нет.
//
// Номер вводится целиком и уходит на сервер, где шифруется; обратно приезжают только
// последние четыре знака. То есть после сохранения проверить введённое глазами уже нельзя —
// поэтому поле номера подписано так, чтобы человек сверился ДО отправки.
//
// Разряд спрашивается только у удостоверения охранника: у паспорта и СНИЛС разряда не бывает,
// и лишнее поле в форме — это лишний повод его заполнить не туда.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import type { DocType, DocumentIn } from '@/shared/api/endpoints/people';
import { DOC_TYPES } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import { Skeleton } from '@/shared/ui/screen-state';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { DOC_TYPE_TITLES, GUARD_CATEGORIES } from '../dictionaries';
import { newIdempotencyKey, useAddDocumentMutation, useEmployeeQuery } from '../queries';

const EDIT_PERMISSION = 'core.employee.edit';

export function DocumentPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const employee = useEmployeeQuery(id);

  const [idempotencyKey] = useState(newIdempotencyKey);
  const [docType, setDocType] = useState<string>('passport');
  const [series, setSeries] = useState('');
  const [number, setNumber] = useState('');
  const [issuedBy, setIssuedBy] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [category, setCategory] = useState(NO_VALUE);
  const [numberError, setNumberError] = useState<string | undefined>(undefined);
  const [gateNote, setGateNote] = useState<string | null>(null);

  const mutation = useAddDocumentMutation(id, idempotencyKey);
  const isLicense = docType === 'guard_license';

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Заводить документы может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Документ уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (number.trim() === '') {
      setNumberError(
        'Введите номер: без него документ нельзя будет сверить — наружу система отдаёт только последние четыре знака.',
      );
      return;
    }
    setNumberError(undefined);
    setGateNote(null);

    const body: DocumentIn = {
      doc_type: docType as DocType,
      series: series.trim() === '' ? null : series.trim(),
      number: number.trim(),
      issued_by: issuedBy.trim() === '' ? null : issuedBy.trim(),
      issued_at: issuedAt === '' ? null : issuedAt,
      expires_at: expiresAt === '' ? null : expiresAt,
      guard_category: isLicense && category !== NO_VALUE ? Number(category) : null,
    };
    mutation.mutate(body, { onSuccess: onClose });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {/* Пока имя едет — скелет, а не слово «загружаем…»: серая строка текста на месте
          будущего имени читается как ЗНАЧЕНИЕ («чьё это заявление? загружаем карточку»), а
          человек в этот момент уже начал заполнять форму. Скелет говорит «сюда приедет», а
          не подставляет ответ. */}
      {/* Скелет показывается ТОЛЬКО пока запрос идёт. Условие «данных нет» держало бы его
          вечно: карточка может не приехать вовсе — например, панель открыли по ссылке на
          человека, которого больше не видно по правам. Вечный скелет читается как «сейчас
          догрузится» и не догружается никогда. */}
      {employee.data === undefined && employee.isPending ? (
        <Skeleton className='h-4 w-40' />
      ) : employee.data === undefined ? null : (
        <p className='text-paragraph-xs text-text-sub-600'>{employee.data.display_name}</p>
      )}

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но документ не сохранится.
            Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <SelectField
        id='document-type'
        label='Тип документа'
        required
        value={docType}
        onChange={setDocType}
        options={DOC_TYPES.map((code) => ({ value: code, label: DOC_TYPE_TITLES[code] }))}
        placeholder='Выберите тип'
      />

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='document-series'
          label='Серия'
          maxLength={16}
          value={series}
          onChange={setSeries}
          placeholder='6714'
        />
        <TextField
          id='document-number'
          label='Номер'
          required
          maxLength={32}
          value={number}
          onChange={setNumber}
          placeholder='123456'
          error={numberError}
        />
      </div>
      <Hint.Root>
        Сверьте номер до сохранения: обратно система отдаёт только последние четыре знака, полный
        номер хранится зашифрованным.
      </Hint.Root>

      <TextField
        id='document-issued-by'
        label='Кем выдан'
        value={issuedBy}
        onChange={setIssuedBy}
        placeholder='ОУФМС России'
      />

      <DateField
        id='document-issued-at'
        label='Дата выдачи'
        value={issuedAt}
        onChange={setIssuedAt}
      />

      <DateField
        id='document-expires-at'
        label='Действует до'
        value={expiresAt}
        onChange={setExpiresAt}
        hint='По этой дате система предупредит, что документ вот-вот истечёт.'
      />

      {isLicense && (
        <SelectField
          id='document-category'
          label='Разряд охранника'
          value={category}
          onChange={setCategory}
          options={GUARD_CATEGORIES.map((value) => ({ value, label: `${value} разряд` }))}
          noneLabel='Разряд не указан'
          placeholder='Выберите разряд'
          hint='Разряд из удостоверения решает, на какие посты человека можно поставить.'
        />
      )}

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Сохраняем документ…' : 'Добавить документ'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
